import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { axiosInstance } from "../../../utils/networking";
import MetricCard from "./MetricCard";
import {
  REFERRAL_PIPELINE_STATUSES,
  pipelineStatusColors,
  pipelineStatusLabels,
} from "../../common/ReferralStatusChip";
import {
  DOLLAR_OUTCOME_TYPES,
  COUNT_OUTCOME_TYPES,
  outcomeTypeLabels,
} from "../../Case/Outcomes/OutcomeEditModal";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

const SectionCard = ({ children, sx }: { children: any; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionTitle = ({
  children,
  subtitle,
}: {
  children: any;
  subtitle?: string;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#2c2c34",
        borderLeft: `4px solid ${PRIMARY}`,
        pl: 1.5,
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", pl: 1.5, mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const pct = (value: number) => `${Math.round((value || 0) * 100)}%`;

interface ExecutiveDashboardProps {
  /**
   * When true the component is rendered inside the Reporting hub: the hub's
   * filter bar owns the date range (passed via `start`/`end`), so the local
   * Start/End/Apply/Reset toolbar and the page background/padding are dropped.
   */
  embedded?: boolean;
  start?: Dayjs | null;
  end?: Dayjs | null;
}

// Admin-only leadership view over GET /reports/executive. The server 403 is
// the real guard — this page just renders the result (or the denial).
const ExecutiveDashboard = ({
  embedded = false,
  start = null,
  end = null,
}: ExecutiveDashboardProps) => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchReport = (start: Dayjs | null, end: Dayjs | null) => {
    setLoading(true);
    let url = `/reports/executive`;
    const params: string[] = [];
    if (start) params.push(`startDate=${start.format("YYYY-MM-DD")}`);
    if (end) params.push(`endDate=${end.format("YYYY-MM-DD")}`);
    if (params.length > 0) url = url + `?` + params.join("&");
    axiosInstance
      .get(url)
      .then((response: any) => {
        setReport(response.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 403) setForbidden(true);
      })
      .finally(() => setLoading(false));
  };

  // In embedded mode the hub's Dayjs objects get new identities on unrelated
  // re-renders, so key the refetch on the formatted dates instead. Standalone
  // keys are constant "" — the effect then runs once on mount, as before.
  const startKey = embedded && start ? start.format("YYYY-MM-DD") : "";
  const endKey = embedded && end ? end.format("YYYY-MM-DD") : "";

  useEffect(() => {
    if (embedded) {
      fetchReport(start, end);
    } else {
      fetchReport(null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, startKey, endKey]);

  // The active range: hub-driven props when embedded, local state otherwise.
  const activeStart = embedded ? start : startDate;
  const activeEnd = embedded ? end : endDate;

  // The hub already wraps tab content in a padded gray page — don't double it.
  const pageSx = embedded
    ? {}
    : {
        backgroundColor: "#f6f7f9",
        p: { xs: 2, md: 3 },
        minHeight: "100%",
      };

  if (forbidden) {
    return (
      <Box sx={pageSx}>
        <SectionCard sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" sx={{ color: "#8a8a93" }}>
            You are not authorized to view the executive report
          </Typography>
        </SectionCard>
      </Box>
    );
  }

  if (loading && !report) {
    return (
      <Box
        sx={{
          ...pageSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!report) {
    return <Box sx={pageSx} />;
  }

  const rangeLabel =
    activeStart || activeEnd
      ? `${activeStart ? activeStart.format("MMM D, YYYY") : "Beginning"} – ${
          activeEnd ? activeEnd.format("MMM D, YYYY") : "Today"
        }`
      : "All time";

  const funnelData = REFERRAL_PIPELINE_STATUSES.map((status) => ({
    key: status,
    name: pipelineStatusLabels[status] || status,
    value: report.referrals.funnel[status] || 0,
  }));

  const impactByType: { [key: string]: { count: number; totalValue: number } } =
    {};
  (report.economicImpact || []).forEach((entry: any) => {
    impactByType[entry.type] = {
      count: entry.count,
      totalValue: entry.totalValue,
    };
  });
  const totalCapital = DOLLAR_OUTCOME_TYPES.reduce(
    (sum, type) => sum + (impactByType[type]?.totalValue || 0),
    0,
  );
  const totalJobs = COUNT_OUTCOME_TYPES.reduce(
    (sum, type) => sum + (impactByType[type]?.totalValue || 0),
    0,
  );
  const hasImpact = (report.economicImpact || []).length > 0;

  const compliance = report.followUpCompliance || { byUser: [], totals: {} };

  return (
    <Box sx={pageSx}>
      {!embedded && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "16px",
            border: CARD_BORDER,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(date) => setStartDate(date)}
              disableFuture={true}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(date) => setEndDate(date)}
              shouldDisableDate={(date: Dayjs) =>
                startDate !== null && date < startDate
              }
              slotProps={{ textField: { size: "small" } }}
            />
          </LocalizationProvider>
          <Button
            variant="contained"
            onClick={() => fetchReport(startDate, endDate)}
            sx={{ backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7A1B4D" } }}
          >
            Apply
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setStartDate(null);
              setEndDate(null);
              fetchReport(null, null);
            }}
          >
            Reset
          </Button>
        </Paper>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#2c2c34" }}>
          Executive Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
          {rangeLabel}
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Clients */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <MetricCard
            label="Active Clients"
            value={report.clients.active}
            accent={PRIMARY}
            sub="open cases now"
          />
          <MetricCard
            label="New Clients"
            value={report.clients.new}
            accent="#3F51B5"
            sub="cases opened in range"
          />
          <MetricCard
            label="Clients Closed"
            value={report.clients.closed}
            accent="#2e7d32"
            sub="cases closed in range"
          />
          <MetricCard
            label="Referrals"
            value={report.referrals.total}
            accent="#fdb814"
          />
          <MetricCard
            label="Referral Completion"
            value={pct(report.referrals.completionRate)}
            accent="#2e7d32"
            sub="of referrals that progressed"
          />
        </Box>

        {/* Economic impact */}
        <SectionCard>
          <SectionTitle subtitle="From recorded client outcomes — businesses started, jobs, capital">
            Economic Impact
          </SectionTitle>
          {!hasImpact ? (
            <Typography sx={{ color: "#8a8a93" }}>
              No outcomes recorded in this period yet. Outcomes are captured on
              the case Outcomes tab and when closing a case.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <MetricCard
                label="Capital Accessed"
                value={`$${totalCapital.toLocaleString()}`}
                accent="#2e7d32"
                sub="grants, loans, capital, revenue"
              />
              <MetricCard
                label="Jobs Created / Retained"
                value={totalJobs.toLocaleString()}
                accent="#3F51B5"
              />
              <MetricCard
                label="Businesses Started"
                value={impactByType["business_started"]?.count || 0}
                accent={PRIMARY}
              />
              <MetricCard
                label="Businesses Expanded"
                value={impactByType["business_expanded"]?.count || 0}
                accent={PRIMARY}
              />
              <MetricCard
                label="Certifications"
                value={impactByType["certification"]?.count || 0}
                accent="#00838f"
              />
            </Box>
          )}
          {hasImpact && (
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Outcome Type</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report.economicImpact || []).map((entry: any) => (
                  <TableRow key={entry.type}>
                    <TableCell>
                      {outcomeTypeLabels[
                        entry.type as keyof typeof outcomeTypeLabels
                      ] || entry.type}
                    </TableCell>
                    <TableCell align="right">{entry.count}</TableCell>
                    <TableCell align="right">
                      {DOLLAR_OUTCOME_TYPES.includes(entry.type)
                        ? `$${(entry.totalValue || 0).toLocaleString()}`
                        : COUNT_OUTCOME_TYPES.includes(entry.type)
                          ? `${(entry.totalValue || 0).toLocaleString()} jobs`
                          : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>

        {/* Referral funnel + top partners */}
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <SectionCard sx={{ flex: "1 1 380px", minWidth: 0 }}>
            <SectionTitle subtitle="Referrals created in range, by current pipeline status">
              Referral Funnel
            </SectionTitle>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={funnelData}
                  margin={{ top: 20, bottom: 40, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-25}
                    tick={{ fontSize: 10, textAnchor: "end" }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" isAnimationActive={false} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="value" position="top" />
                    {funnelData.map((entry) => (
                      <Cell
                        key={entry.key}
                        fill={pipelineStatusColors[entry.key] || PRIMARY}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>

          <SectionCard sx={{ flex: "1 1 380px", minWidth: 0 }}>
            <SectionTitle subtitle="Full breakdown in Reports → Ecosystem Report">
              Top Partners
            </SectionTitle>
            {/* Scrolls sideways on its own when wider than the card. */}
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Partner</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Referrals</TableCell>
                    <TableCell align="right">Completed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(report.topPartners || []).map((partner: any) => (
                    <TableRow key={partner.id}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {partner.name}
                      </TableCell>
                      <TableCell>{partner.category}</TableCell>
                      <TableCell align="right">{partner.total}</TableCell>
                      <TableCell align="right">
                        {partner.byStatus["completed"] || 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Box>

        {/* Caseload */}
        <SectionCard>
          <SectionTitle subtitle="Open primary cases and secondary assignments per consultant">
            Caseload by Consultant
          </SectionTitle>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Consultant</TableCell>
                <TableCell align="right">Primary Open Cases</TableCell>
                <TableCell align="right">Secondary Cases</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(report.caseload || []).map((consultant: any) => (
                <TableRow key={consultant.user_id}>
                  <TableCell sx={{ fontWeight: 600 }}>{consultant.name}</TableCell>
                  <TableCell align="right">{consultant.primaryOpen}</TableCell>
                  <TableCell align="right">{consultant.secondary}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>

        {/* Follow-up compliance */}
        <SectionCard>
          <SectionTitle subtitle="Rule-generated follow-up reminders: completion and overdue rates per consultant">
            Follow-Up Compliance
          </SectionTitle>
          {compliance.byUser.length === 0 ? (
            <Typography sx={{ color: "#8a8a93" }}>
              No automated follow-ups generated yet — this fills in once the
              nightly follow-up job is live.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Consultant</TableCell>
                  <TableCell align="right">Follow-Ups</TableCell>
                  <TableCell align="right">Completed</TableCell>
                  <TableCell align="right">% Completed</TableCell>
                  <TableCell align="right">% Within 7 Days</TableCell>
                  <TableCell align="right">Pending Overdue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {compliance.byUser.map((entry: any) => (
                  <TableRow key={entry.user_id}>
                    <TableCell sx={{ fontWeight: 600 }}>{entry.name}</TableCell>
                    <TableCell align="right">{entry.total}</TableCell>
                    <TableCell align="right">{entry.completed}</TableCell>
                    <TableCell align="right">{pct(entry.pctCompleted)}</TableCell>
                    <TableCell align="right">
                      {pct(entry.pctCompletedWithin7d)}
                    </TableCell>
                    <TableCell align="right">{entry.pendingOverdue}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>All Consultants</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {compliance.totals.total}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {compliance.totals.completed}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {pct(compliance.totals.pctCompleted)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {pct(compliance.totals.pctCompletedWithin7d)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {compliance.totals.pendingOverdue}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </SectionCard>
      </Stack>
    </Box>
  );
};

export default ExecutiveDashboard;
